import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  viewChild,
  inject,
  effect,
  signal,
  untracked,
  ChangeDetectionStrategy,
  OnDestroy,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { extend, injectStore } from 'angular-three';
import { NgtsHTML, NgtsHTMLContent, NgtsHTMLOptions } from 'angular-three-soba/misc';
import { NgtsOrbitControls } from 'angular-three-soba/controls';
import { ArService } from '../../services/ar.service';
import * as THREE from 'three';
import {
  BoxGeometry,
  MeshStandardMaterial,
  AmbientLight,
  DirectionalLight,
  PointLight,
} from 'three';
import { PollGameState, POLL_TABLET_WIDTH_PX, POLL_TABLET_HEIGHT_PX, POLL_TABLET_DEPTH } from './poll.types';
import { PollQuestionTally } from '@kanek/shared-data-access';
import { calculateHTMLDimensions3D } from '../../utils/three-scene.utils';
import { EMPIRICAL_CORRECTIONS } from '../../constants/three-scene.constants';
import Chart from 'chart.js/auto';

// Extend Three.js objects for Angular Three template rendering
extend({
  BoxGeometry,
  MeshStandardMaterial,
  AmbientLight,
  DirectionalLight,
  PointLight,
});

/** Frame overhang beyond the tablet face on each edge (world units) */
const FRAME_OVERHANG = 0.18;

/**
 * Poll Scene Component
 *
 * Declarative Angular Three component that renders the poll game in 3D:
 * - A glass tablet base (dark semi-transparent BoxGeometry with MeshStandardMaterial)
 * - A neon cyan emissive frame (slightly larger box, cyberpunk aesthetic)
 * - An HTML overlay (ngts-html, occlude: 'blending') showing the question UI
 *
 * The scene communicates with PollRenderer via `scene.userData`:
 *   - `scene.userData['setPollGameState']`  — renderer → scene (push new state)
 *   - `scene.userData['onOptionSelected']`  — scene → renderer (user tapped option)
 *   - `scene.userData['orbitControls']`     — scene → parent (camera access)
 *
 * Follows the established Angular Three html-in-3d pattern:
 *   - HTML overlay is the LAST child of ngt-group
 *   - HTML rotation always [0, 0, 0]
 *   - occlude: 'blending' (not true)
 *   - All geometry + material use `ngt-value` for proper occlusion blending
 */
@Component({
  selector: 'lib-poll-scene',
  standalone: true,
  imports: [NgClass, NgtsHTML, NgtsHTMLContent, NgtsOrbitControls],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    :host { display: contents; }
  `],
  template: `
    <!-- Lighting ─────────────────────────────────────────────────────── -->
    <ngt-ambient-light [intensity]="0.6" />

    <!-- Key directional light -->
    <ngt-directional-light [position]="[3, 5, 4]" [intensity]="1.8" />

    <!-- Fill light -->
    <ngt-directional-light [position]="[-2, 3, -2]" [intensity]="0.6" />

    <!-- Neon point light for the glow effect (accent color, in front of tablet) -->
    <ngt-point-light
      [position]="[0, 0, 3]"
      [color]="neonPointLightColor()"
      [intensity]="1.2"
      [distance]="12"
    />

    <!-- OrbitControls (preview mode only) -->
    @if (enableCameraControls()) {
      <ngts-orbit-controls
        [options]="{ enableDamping: true, dampingFactor: 0.05, enablePan: true }"
      />
    }

    <!-- Scene group (rotated for AR flat mode) ────────────────────────── -->
    <ngt-group [rotation]="sceneGroupRotation()">

      <!-- 1. Neon frame (slightly larger than tablet, emissive glow) -->
      <ngt-mesh [position]="[0, 0, 0]">
        <ngt-value [rawValue]="frameGeometry()" attach="geometry" />
        <ngt-value [rawValue]="frameMaterial()" attach="material" />
      </ngt-mesh>

      <!-- 2. Glass tablet body -->
      <ngt-mesh [position]="[0, 0, 0]">
        <ngt-value [rawValue]="tabletGeometry()" attach="geometry" />
        <ngt-value [rawValue]="tabletMaterial()" attach="material" />
      </ngt-mesh>

      <!-- 3. HTML overlay — MUST be last in group for correct occlusion ordering -->
      <ngts-html [options]="htmlOverlayOptions()">
        <!-- Occlusion plane (auto-sized by ngts-html to match HTML content) -->
        <ngt-plane-geometry data-occlusion-geometry />
        <ngt-mesh-basic-material data-occlusion-material [colorWrite]="false" />

        <div [ngtsHTMLContent]="htmlContentOptions()">
          <div
            class="poll-tablet-ui"
            [ngClass]="tabletStyleClass()"
            [style.width]="tabletWidthPx() + 'px'"
            [style.height]="tabletHeightPx() + 'px'"
          >
            @if (currentMode() === 'question' || currentMode() === 'idle') {
              <!-- ── Question mode ─────────────────────────────────────── -->
              <div class="question-progress">
                Question {{ (currentQuestionIndex() + 1) }} / {{ totalQuestions() }}
              </div>

              <div class="question-text">{{ questionText() }}</div>

              <div class="options-list">
                @for (option of currentOptions(); track option.id) {
                  <button
                    class="option-btn"
                    [class.selected]="isOptionSelected(option.id)"
                    (click)="onOptionTap(option.id)"
                  >
                    <span>{{ option.text }}</span>
                    @if (isOptionSelected(option.id)) {
                      <span class="option-check">✓</span>
                    }
                  </button>
                }
              </div>

              @if (showValidationHint()) {
                <div class="validation-hint">Please select an answer to continue</div>
              }
            }

            @if (currentMode() === 'results') {
              <!-- ── Results mode ──────────────────────────────────────── -->
              <div class="question-progress">
                Question {{ (currentQuestionIndex() + 1) }} / {{ totalQuestions() }}
              </div>

              <div class="question-text">{{ questionText() }}</div>

              <div class="results-header">Results so far</div>

              <div class="results-chart-container">
                <canvas #resultsChart></canvas>
              </div>
            }
          </div>
        </div>
      </ngts-html>

    </ngt-group>
  `,
})
export class PollSceneComponent implements OnDestroy {
  protected readonly tabletWidthPx = computed(() => this.settingsData()?.tabletWidth ?? POLL_TABLET_WIDTH_PX);
  protected readonly tabletHeightPx = computed(() => this.settingsData()?.tabletHeight ?? POLL_TABLET_HEIGHT_PX);
  protected readonly tabletDepth = computed(() => this.settingsData()?.tabletDepth ?? POLL_TABLET_DEPTH);

  /** Accent color for neon point light — uses accentColor setting when set, otherwise style default */
  protected readonly neonPointLightColor = computed(() => {
    const settings = this.settingsData();
    if (settings?.accentColor) return settings.accentColor;
    const styleDefaults: Record<string, string> = {
      cyberpunk: '#00e5ff',
      minimal: '#6060ff',
      holographic: '#00ffaa',
    };
    return styleDefaults[settings?.tabletStyle ?? 'cyberpunk'] ?? '#00e5ff';
  });

  // ── DI ──────────────────────────────────────────────────────────────────────
  protected readonly store = injectStore();
  protected readonly arService = inject(ArService);

  // ── ViewChild ───────────────────────────────────────────────────────────────
  protected readonly orbitControlsRef = viewChild(NgtsOrbitControls);
  private readonly resultsChartRef = viewChild<ElementRef<HTMLCanvasElement>>('resultsChart');

  // ── Game state (pushed by PollRenderer) ─────────────────────────────────────
  private readonly gameState = signal<PollGameState | null>(null);

  // ── Chart.js instance ────────────────────────────────────────────────────────
  private chart: Chart | null = null;

  // ── Derived display state ────────────────────────────────────────────────────
  protected readonly currentMode = computed(() => this.gameState()?.mode ?? 'idle');
  protected readonly currentQuestionIndex = computed(() => this.gameState()?.currentQuestionIndex ?? 0);
  protected readonly totalQuestions = computed(() => this.gameState()?.totalQuestions ?? 0);
  protected readonly showValidationHint = computed(() => this.gameState()?.showValidationHint ?? false);

  protected readonly questionText = computed(() => {
    const state = this.gameState();
    if (!state) return '';
    return state.questions[state.currentQuestionIndex]?.text ?? '';
  });

  protected readonly currentOptions = computed(() => {
    const state = this.gameState();
    if (!state) return [];
    return state.questions[state.currentQuestionIndex]?.options ?? [];
  });

  protected readonly currentTally = computed(() => this.gameState()?.questionTally ?? null);

  // ── 3D Geometry ─────────────────────────────────────────────────────────────

  protected readonly tabletDimensions3D = computed(() => {
    // Use untracked() so this computed is NOT reactive to per-frame NgtStore
    // updates (clock, performance stats, etc.). The tablet pixel dimensions are
    // compile-time constants; re-evaluating on every frame would create a new
    // BoxGeometry/MeshStandardMaterial on every frame without disposing the
    // previous ones, causing GPU memory pressure and CPU spikes.
    // The only time we truly need to recompute is on viewport resize — which is
    // handled by destroying/recreating the canvas, so a one-time snapshot is fine.
    return calculateHTMLDimensions3D(
      this.tabletWidthPx(),
      this.tabletHeightPx(),
      untracked(() => this.store.get()),
    );
  });

  protected readonly tabletGeometry = computed(() => {
    const d = this.tabletDimensions3D();
    return new BoxGeometry(d.width, d.height, this.tabletDepth());
  });

  protected readonly frameGeometry = computed(() => {
    const d = this.tabletDimensions3D();
    // Frame is slightly larger than the tablet on all sides
    return new BoxGeometry(
      d.width + FRAME_OVERHANG,
      d.height + FRAME_OVERHANG,
      this.tabletDepth() + 0.05,
    );
  });

  protected readonly tabletMaterial = computed(() => {
    const settings = this.settingsData();
    const style = (settings?.tabletStyle ?? 'cyberpunk') as string;
    const colors: Record<string, number> = {
      cyberpunk:    0x061428,
      minimal:      0x12121c,
      holographic:  0x001932,
    };
    return new MeshStandardMaterial({
      color: colors[style] ?? colors['cyberpunk'],
      transparent: true,
      opacity: 0.88,
      roughness: 0.08,
      metalness: 0.15,
      emissive: 0x001a2e,
      emissiveIntensity: 0.4,
      side: THREE.FrontSide,
    });
  });

  protected readonly frameMaterial = computed(() => {
    const settings = this.settingsData();
    const style = (settings?.tabletStyle ?? 'cyberpunk') as string;
    const styleColors: Record<string, number> = {
      cyberpunk:    0x00e5ff,
      minimal:      0x6060ff,
      holographic:  0x00ffaa,
    };
    const styleColor = styleColors[style] ?? styleColors['cyberpunk'];
    const emissive = settings?.accentColor
      ? new THREE.Color(settings.accentColor).getHex()
      : styleColor;
    return new MeshStandardMaterial({
      color: emissive,
      emissive: emissive,
      emissiveIntensity: 1.6,
      roughness: 0.0,
      metalness: 1.0,
      transparent: true,
      opacity: 0.1,
    });
  });

  // ── HTML Overlay options ─────────────────────────────────────────────────────

  protected readonly htmlOverlayOptions = computed((): Partial<NgtsHTMLOptions> => {
    const zOffset = this.tabletDepth() / 2 + 0.01;
    return {
      occlude: 'blending',
      transform: true,
      scale: 1.0,
      position: [0, 0, zOffset] as [number, number, number],
      rotation: [0, 0, 0] as [number, number, number], // NEVER rotate HTML
    };
  });

  protected readonly htmlContentOptions = computed(() => ({
    distanceFactor: 0,
    zIndexRange: [100, 1] as [number, number],
  }));

  // ── Scene group rotation (AR vs orbit-controls mode) ───────────────────────

  protected readonly sceneGroupRotation = computed<[number, number, number]>(() => {
    const enableAR = this.store.get().scene?.userData?.['enableAR'] ?? false;
    const bypassActive = this.arService.shouldBypassAR;

    if (bypassActive || !enableAR) {
      return [0, 0, 0]; // Upright in orbit-controls mode
    }
    // AR mode: lay flat on the marker
    return [-Math.PI / 2, 0, 0];
  }, {
    // Custom equality: compare tuple values rather than array reference identity.
    // Without this, every evaluation returns a *new* array, Angular treats it as
    // changed, and ngt-group re-sets its rotation prop on every CD cycle.
    equal: (a, b) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2],
  });

  // ── Camera controls ─────────────────────────────────────────────────────────

  protected readonly enableCameraControls = computed(() => {
    const fromUserData = this.store.get().scene?.userData?.['enableCameraControls'] ?? false;
    const bypassActive = this.arService.shouldBypassAR;
    return bypassActive || fromUserData;
  });

  // ── Tablet style class ───────────────────────────────────────────────────────

  protected readonly tabletStyleClass = computed(() => {
    const style = this.settingsData()?.tabletStyle ?? 'cyberpunk';
    return `style-${style}`;
  });

  // ── Settings from scene.userData ────────────────────────────────────────────

  protected readonly settingsData = computed(() => {
    // Read scene via untracked to avoid subscribing to 60fps NgtStore clock updates.
    // The configSignal itself is still called in tracked context so reactive config changes work.
    const scene = untracked(() => this.store.get()).scene;
    const configSignal = scene?.userData?.['configSignal'];
    const config = configSignal ? configSignal() : null;
    return (config as any)?.settings ?? null;
  });

  constructor() {
    this.injectStyles();

    // Register the game-state setter and perform a one-shot initial state sync.
    //
    // IMPORTANT: this effect subscribes to NgtStore via `this.store.get()`, which
    // means it can re-fire on every Three.js render frame (clock / performance
    // updates). We MUST NOT call refresh() on each re-run — doing so would call
    // buildAndPushState() at 60 Hz, pushing new PollGameState objects every frame
    // and mass-triggering Angular change detection, causing 100 % CPU usage.
    //
    // hasRefreshed guards the one-shot seed: it fires exactly once as soon as
    // both the scene AND the renderer's requestPollStateRefresh callback are ready.
    let hasRefreshed = false;
    effect(() => {
      const scene = this.store.get().scene;
      if (scene) {
        // Idempotent callback registration — safe to overwrite on re-runs.
        // With ChangeDetectionStrategy.OnPush + signals, gameState.set() already
        // schedules Angular CD automatically; cdr.detectChanges() is not needed
        // and would cause redundant synchronous change detection passes.
        scene.userData['setPollGameState'] = (state: PollGameState) => {
          this.gameState.set(state);
        };

        // One-shot initial sync: seeds the scene with the renderer's current
        // state to handle the timing race where the renderer's buildAndPushState()
        // fired before we had a chance to register the setter above.
        if (!hasRefreshed) {
          const refresh = scene.userData['requestPollStateRefresh'];
          if (typeof refresh === 'function') {
            hasRefreshed = true;
            refresh();
          }
        }
      }
    });

    // Store OrbitControls in scene.userData (required pattern for all game scenes)
    effect(() => {
      const controls = this.orbitControlsRef()?.controls();
      const scene = this.store.snapshot.scene;
      if (controls && scene) {
        scene.userData['orbitControls'] = controls;
      }
    });

    // Manage Chart.js lifecycle based on game state changes
    effect(() => {
      const mode = this.currentMode();
      const tally = this.currentTally();

      if (mode !== 'results' || !tally) {
        this.destroyChart();
        return;
      }

      // Defer chart creation until after Angular renders the canvas element
      setTimeout(() => {
        const canvas = this.resultsChartRef()?.nativeElement;
        if (canvas) {
          this.renderResultsChart(canvas, tally);
        }
      }, 60);
    });
  }

  // ── User interaction ─────────────────────────────────────────────────────────

  /**
   * Inject poll overlay styles into <head> as a global <style> tag.
   * NgtsHTMLContent renders HTML outside the Angular component tree, so
   * Angular-scoped component styles (with [_ngcontent-xxx] attributes) are
   * never applied to those elements. Injecting globally bypasses encapsulation,
   * matching the same pattern used by game-board-renderer.ts.
   */
  private injectStyles(): void {
    const styleId = 'poll-scene-styles';
    if (document.getElementById(styleId)) return; // already injected

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .poll-tablet-ui {
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        padding: 32px 28px 24px;
        font-family: 'Segoe UI', 'Rajdhani', Arial, sans-serif;
        color: #e0f0ff;
        user-select: none;
        overflow: hidden;
        border-radius: 6px;
      }

      /* ── Cyberpunk style ─────────────────── */
      .poll-tablet-ui.style-cyberpunk {
        background: rgba(6, 16, 38, 0.93);
        box-shadow:
          0 0 0 2px #00e5ff,
          0 0 14px 2px rgba(0, 229, 255, 0.70),
          0 0 40px 4px rgba(0, 229, 255, 0.25);
      }

      /* ── Minimal style ───────────────────── */
      .poll-tablet-ui.style-minimal {
        background: rgba(18, 18, 28, 0.88);
        box-shadow:
          0 0 0 2px #6060ff,
          0 0 14px 2px rgba(96, 96, 255, 0.55);
      }

      /* ── Holographic style ───────────────── */
      .poll-tablet-ui.style-holographic {
        background: rgba(0, 25, 50, 0.80);
        box-shadow:
          0 0 0 2px #00ffaa,
          0 0 14px 2px rgba(0, 255, 170, 0.55);
      }

      .question-progress {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 2px;
        text-transform: uppercase;
        color: #00e5ff;
        margin-bottom: 14px;
        opacity: 0.85;
      }

      .question-text {
        font-size: 21px;
        font-weight: 600;
        line-height: 1.5;
        margin-bottom: 26px;
        color: #ffffff;
        flex-shrink: 0;
      }

      .options-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
        flex: 1;
      }

      .option-btn {
        background: rgba(0, 229, 255, 0.04);
        border: 1.5px solid rgba(0, 229, 255, 0.25);
        border-radius: 8px;
        padding: 15px 18px;
        color: #c0e8ff;
        font-size: 16px;
        font-family: inherit;
        cursor: pointer;
        text-align: left;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        transition: background 0.12s ease, border-color 0.12s ease;
        -webkit-tap-highlight-color: transparent;
        outline: none;
      }

      .option-btn:hover {
        background: rgba(0, 229, 255, 0.10);
        border-color: rgba(0, 229, 255, 0.65);
        color: #ffffff;
      }

      .option-btn.selected {
        background: rgba(0, 229, 255, 0.15);
        border-color: #00e5ff;
        color: #ffffff;
        box-shadow:
          0 0 10px rgba(0, 229, 255, 0.28),
          inset 0 0 6px rgba(0, 229, 255, 0.08);
      }

      .option-check {
        color: #00e5ff;
        font-size: 17px;
        font-weight: 700;
        flex-shrink: 0;
      }

      .validation-hint {
        color: #ff7b7b;
        font-size: 13px;
        text-align: center;
        margin-top: 14px;
        letter-spacing: 0.5px;
        animation: poll-hint-pulse 1.2s ease-in-out infinite;
      }

      @keyframes poll-hint-pulse {
        0%, 100% { opacity: 1; }
        50%       { opacity: 0.45; }
      }

      /* ── Results mode ────────────────────── */
      .results-header {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 2px;
        text-transform: uppercase;
        color: #00e5ff;
        margin-bottom: 12px;
      }

      .results-chart-container {
        flex: 1;
        display: flex;
        align-items: center;
        min-height: 0;
        padding-bottom: 8px;
      }

      .results-chart-container canvas {
        width: 100% !important;
        max-height: 340px;
      }
    `;
    document.head.appendChild(style);
  }

  protected onOptionTap(optionId: string): void {
    const scene = this.store.get().scene;
    const callback = scene?.userData?.['onOptionSelected'];
    if (typeof callback === 'function') {
      callback(optionId);
    }
  }

  protected isOptionSelected(optionId: string): boolean {
    return this.gameState()?.userSelectedOptionIds?.includes(optionId) ?? false;
  }

  // ── Chart.js helpers ─────────────────────────────────────────────────────────

  private renderResultsChart(
    canvas: HTMLCanvasElement,
    tally: PollQuestionTally,
  ): void {
    this.destroyChart();

    const userSelections = this.gameState()?.userSelectedOptionIds ?? [];

    this.chart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: tally.options.map((o) => o.optionText),
        datasets: [
          {
            data: tally.options.map((o) => o.percentage),
            backgroundColor: tally.options.map((o) =>
              userSelections.includes(o.optionId)
                ? 'rgba(0, 229, 255, 0.55)'
                : 'rgba(60, 100, 140, 0.60)',
            ),
            borderColor: tally.options.map((o) =>
              userSelections.includes(o.optionId)
                ? '#00e5ff'
                : 'rgba(80, 130, 180, 0.8)',
            ),
            borderWidth: tally.options.map((o) =>
              userSelections.includes(o.optionId) ? 2 : 1,
            ),
            borderRadius: 4,
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 600, easing: 'easeOutQuart' },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => ` ${ctx.raw}%`,
            },
            backgroundColor: 'rgba(6, 16, 38, 0.9)',
            titleColor: '#00e5ff',
            bodyColor: '#c0e8ff',
          },
        },
        scales: {
          x: {
            min: 0,
            max: 100,
            grid: { color: 'rgba(0, 229, 255, 0.08)' },
            ticks: {
              color: '#7098b8',
              font: { size: 11 },
              callback: (val) => `${val}%`,
            },
          },
          y: {
            grid: { display: false },
            ticks: {
              color: '#b0d0f0',
              font: { size: 13 },
            },
          },
        },
      },
    });
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  ngOnDestroy(): void {
    // Dispose Three.js GPU resources that are not managed by angular-three.
    // Without explicit disposal, stale geometry/material buffers accumulate in
    // the WebGL context each time the poll game is mounted/unmounted.
    this.tabletGeometry()?.dispose();
    this.frameGeometry()?.dispose();
    this.tabletMaterial()?.dispose();
    this.frameMaterial()?.dispose();
    this.destroyChart();
  }
}
